import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Grid,
  Paper,
  Avatar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  useGetRolesQuery,
  usePostRoleMutation,
  useUpdateRoleMutation,
} from "../../redux/api/rolesApi";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { Controller, FormProvider, useForm } from "react-hook-form";
import AutoComplete from "../../components/FormElements/AutoComplete";
import { LockOutlined, CheckCircleOutline } from "@mui/icons-material";
import { ROLES_PERMISSIONS } from "./permissions";
import { toast } from "react-toastify";

export default function AddRoles({ open, onClose, selectedRole, refetch }) {
  const currentAgency = useSelector(selectCurrentagency);

  const methods = useForm({
    defaultValues: {
      roleId: "",
      // Property Management
      addProperty: false,
      viewProperty: false,
      updateProperty: false,
      // Lead Management
      addLead: false,
      updateLead: false,
      viewpool: false,
      assignlead: false,
      viewlead: false,
      viewalllead: false,
      uploadleads: false,
      // Financial
      genVoucher: false,
      // Data Access
      viewContacts: false,
      viewAccounts: false,
      // Staff Management
      addstaff: false,
      // Task Management
      assigntask: false,
      viewalltask: false,
      viewpool: false,
      propertylocation: false,
      ownercontact: false,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;
  const { data: roles, isFetching } = useGetRolesQuery();
  const [postRole] = usePostRoleMutation();
  const [updateRole] = useUpdateRoleMutation();

  useEffect(() => {
    if (selectedRole) {
      reset({
        roleId: selectedRole.roleId,
        // Property Management
        addProperty: Boolean(selectedRole.addProperty),
        viewProperty: Boolean(selectedRole.viewProperty),
        updateProperty: Boolean(selectedRole.updateProperty),
        // Lead Management
        addLead: Boolean(selectedRole.addLead),
        updateLead: Boolean(selectedRole.updateLead),
        viewpool: Boolean(selectedRole.viewpool),
        assignlead: Boolean(selectedRole.assignlead),
        viewlead: Boolean(selectedRole.viewlead),
        viewalllead: Boolean(selectedRole.viewalllead),
        uploadleads: Boolean(selectedRole.uploadleads),
        // Financial
        genVoucher: Boolean(selectedRole.genVoucher),
        // Data Access
        viewContacts: Boolean(selectedRole.viewContacts),
        viewAccounts: Boolean(selectedRole.viewAccounts),
        // Staff Management
        addstaff: Boolean(selectedRole.addstaff),
        // Task Management
        assigntask: Boolean(selectedRole.assigntask),
        viewalltask: Boolean(selectedRole.viewalltask),
        viewpool: Boolean(selectedRole.viewpool),
        propertylocation: Boolean(selectedRole.propertylocation),
        ownercontact: Boolean(selectedRole.ownercontact),
      });
    } else {
      reset({
        roleId: "",
        // Property Management
        addProperty: false,
        viewProperty: false,
        updateProperty: false,
        // Lead Management
        addLead: false,
        updateLead: false,
        viewpool: false,
        assignlead: false,
        viewlead: false,
        viewalllead: false,
        uploadleads: false,
        // Financial
        genVoucher: false,
        // Data Access
        viewContacts: false,
        viewAccounts: false,
        // Staff Management
        addstaff: false,
        // Task Management
        assigntask: false,
        viewalltask: false,
        viewpool: false,
        propertylocation: false,
        ownercontact: false,
      });
    }
  }, [selectedRole, reset]);

  const onSubmit = async (data) => {
    const formattedData = {
      roleId: data.roleId,
      agencyId: currentAgency.agencyid,
      // Property Management
      addProperty: data.addProperty ? 1 : 0,
      viewProperty: data.viewProperty ? 1 : 0,
      updateProperty: data.updateProperty ? 1 : 0,
      // Lead Management
      addLead: data.addLead ? 1 : 0,
      updateLead: data.updateLead ? 1 : 0,
      viewpool: data.viewpool ? 1 : 0,
      assignlead: data.assignlead ? 1 : 0,
      viewlead: data.viewlead ? 1 : 0,
      viewalllead: data.viewalllead ? 1 : 0,
      uploadleads: data.uploadleads ? 1 : 0,
      // Financial
      genVoucher: data.genVoucher ? 1 : 0,
      // Data Access
      viewContacts: data.viewContacts ? 1 : 0,
      viewAccounts: data.viewAccounts ? 1 : 0,
      // Staff Management
      addstaff: data.addstaff ? 1 : 0,
      // Task Management
      assigntask: data.assigntask ? 1 : 0,
      viewalltask: data.viewalltask ? 1 : 0,
      viewpool: data.viewpool ? 1 : 0,
      propertylocation: data.propertylocation ? 1 : 0,
      ownercontact: data.ownercontact ? 1 : 0,
      rightId: selectedRole?.rightId,
    };

    try {
      if (selectedRole) {
        await updateRole({
          id: selectedRole.rightId,
          data: formattedData,
        }).unwrap();
      } else {
        await postRole(formattedData).unwrap();
      }
      refetch();
      handleClose();
    } catch (error) {
      console.error("Error assigning role:", error);

      const errorMsg = error?.data?.message || error?.data?.Message;

      if (error?.status === 400 && errorMsg) {
        toast.error(errorMsg);
      } else {
        toast.error("Something went wrong while assigning the role.");
      }
      
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
            <LockOutlined />
          </Avatar>
          <Typography variant="h3" component="div">
            {selectedRole ? "Edit Role Permissions" : "Assign New Role"}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Configure access rights for different roles in your organization
        </Typography>
      </DialogTitle>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            {!selectedRole ? (
              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Select Role
                </Typography>
                <Controller
                  name="roleId"
                  control={control}
                  rules={{ required: "Role is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <AutoComplete
                        {...field}
                        isLoading={isFetching}
                        options={
                          roles?.map((role) => ({
                            roleId: role.roleId,
                            roleName: role.roleName,
                          })) || []
                        }
                        label="Choose a role"
                        optionId="roleId"
                        optionLabel="roleName"
                        error={!!error}
                        onChange={(e, newValue) => {
                          field.onChange(newValue?.roleId || "");
                        }}
                      />
                    </>
                  )}
                />
              </Box>
            ) : null}

            <Divider sx={{ my: 2 }} />

            <Box mt={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Permissions Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Enable or disable specific permissions for this role
              </Typography>

              <Grid container spacing={3}>
                {ROLES_PERMISSIONS.map((group) => (
                  <Grid item xs={12} md={6} key={group.category}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        {group.icon}
                        <Typography variant="subtitle2" fontWeight={600} ml={1}>
                          {group.category}
                        </Typography>
                      </Box>

                      <Box sx={{ pl: 1 }}>
                        {group.permissions.map((perm) => (
                          <Controller
                            key={perm.name}
                            name={perm.name}
                            control={control}
                            render={({ field }) => (
                              <Tooltip
                                title={perm.description}
                                arrow
                                placement="right"
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1,
                                    mb: 1,
                                    borderRadius: 1,
                                    backgroundColor: field.value
                                      ? "#e8f5e9"
                                      : "transparent",
                                    transition: "background-color 0.3s ease",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        {...field}
                                        checked={field.value}
                                        icon={
                                          <CheckCircleOutline color="disabled" />
                                        }
                                        checkedIcon={
                                          <CheckCircleOutline color="success" />
                                        }
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2">
                                          {perm.label}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {perm.description}
                                        </Typography>
                                      </Box>
                                    }
                                    sx={{ width: "100%" }}
                                  />
                                </Paper>
                              </Tooltip>
                            )}
                          />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              borderTop: "1px solid #e0e0e0",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ minWidth: 120, color: "white" }}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
